import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Mail } from 'lucide-react';
import { authAPI } from '../../services/api';
import Button from '../../components/UI/Button';
import LoadingSpinner from '../../components/UI/LoadingSpinner';

const EmailVerification = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('');
  const verificationAttempted = React.useRef(false);

  useEffect(() => {
    const verifyEmail = async () => {
      if (verificationAttempted.current) {
        return;
      }
      verificationAttempted.current = true;

      try {
        if (token) {
          await authAPI.verifyEmail(token);
          setStatus('success');
          setMessage('Email verified successfully! You can now sign in to your account.');
        } else {
          setStatus('error');
          setMessage('No verification token found. Please check your email and try again.');
        }
      } catch (error) {
        setStatus('error');
        // Get specific error message from DRF response
        const errorData = error.response?.data;
        let specificMessage = 'Email verification failed. Please try again.';
        if (errorData) {
          // Find the first error message in the response object
          const firstErrorKey = Object.keys(errorData)[0];
          if (firstErrorKey && Array.isArray(errorData[firstErrorKey])) {
            specificMessage = errorData[firstErrorKey][0];
          } else if (typeof errorData.error === 'string') {
            specificMessage = errorData.error;
          }
        }
        setMessage(specificMessage);
      }
    };

    if (token) {
      verifyEmail();
    }
  }, [token]);

  const handleResendVerification = async () => {
    try {
      await authAPI.resendVerification();
      setMessage('Verification email sent successfully. Please check your inbox.');
    } catch (error) {
      setMessage(error.response?.data?.error || 'Failed to resend verification email.');
    }
  };

  if (status === 'verifying') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <LoadingSpinner size="lg" showText text="Verifying your email..." />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          {status === 'success' ? (
            <CheckCircle className="mx-auto h-12 w-12 text-success-600" />
          ) : (
            <XCircle className="mx-auto h-12 w-12 text-danger-600" />
          )}
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            {status === 'success' ? 'Email Verified!' : 'Verification Failed'}
          </h2>
          <p className="mt-2 text-sm text-gray-600">{message}</p>
        </div>

        <div className="space-y-4">
          {status === 'success' ? (
            <Button
              onClick={() => navigate('/login')}
              variant="primary"
              size="lg"
              fullWidth
            >
              Sign In
            </Button>
          ) : (
            <>
              <Button
                onClick={handleResendVerification}
                variant="primary"
                size="lg"
                fullWidth
              >
                Resend Verification Email
              </Button>
              <Button
                onClick={() => navigate('/login')}
                variant="secondary"
                size="lg"
                fullWidth
              >
                Back to Sign In
              </Button>
            </>
          )}
        </div>

        <div className="text-center">
          <p className="text-sm text-gray-600">
            Need help?{' '}
            <Link
              to="/contact"
              className="font-medium text-primary-600 hover:text-primary-500"
            >
              Contact support
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default EmailVerification; 